"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import ItemOrders from "@/components/Cards/Cards";
import { motion } from "framer-motion";
import {
  Newspaper,
  Mic,
  FileText,
  GraduationCap,
  Globe,
  ChevronRight,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const Sections = ({ data }) => {
  const [categories, setCategories] = useState({
    dialogues: data[0]?.data || [],
    journalism: data[1]?.data || [],
    training: data[2]?.data || [],
    reports: data[3]?.data || [],
    localisation: data[4]?.data || [],
  });

  const [hoveredSection, setHoveredSection] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  const sectionConfig = [
    {
      key: "dialogues",
      index: 0,
      name: "Dialogues",
      icon: Mic,
      color: "from-blue-500 to-cyan-400",
      description: "Community discussions and conversations",
      count: categories.dialogues?.length || 0,
    },
    {
      key: "journalism",
      index: 1,
      name: "Journalism",
      icon: Newspaper,
      color: "from-rose-500 to-pink-400",
      description: "News articles and investigative reports",
      count: categories.journalism?.length || 0,
    },
    {
      key: "training",
      index: 2,
      name: "Training",
      icon: GraduationCap,
      color: "from-amber-500 to-orange-400",
      description: "Educational resources and workshops",
      count: categories.training?.length || 0,
    },
    {
      key: "reports",
      index: 3,
      name: "Reports",
      icon: FileText,
      color: "from-emerald-500 to-teal-400",
      description: "Research papers and analysis",
      count: categories.reports?.length || 0,
    },
    {
      key: "localisation",
      index: 4,
      name: "Localisation",
      icon: Globe,
      color: "from-violet-500 to-purple-400",
      description: "Local content and regional insights",
      count: categories.localisation?.length || 0,
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      y: -5,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 py-12">
      {/* Header */}
      <div className="container mx-auto px-4 mb-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Latest Updates</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            News &{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
              Content
            </span>
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            Stay informed with our latest articles, reports, and insights across
            different categories
          </p>
        </motion.div>

        {/* Section Tabs */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          {sectionConfig.map((section) => {
            const Icon = section.icon;
            return (
              <motion.button
                key={section.key}
                variants={sectionVariants}
                whileHover="hover"
                onClick={() => setActiveSection(section.key)}
                onMouseEnter={() => setHoveredSection(section.key)}
                onMouseLeave={() => setHoveredSection(null)}
                className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                  activeSection === section.key ||
                  hoveredSection === section.key
                    ? `bg-gradient-to-r ${section.color} text-white shadow-lg`
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{section.name}</span>
                {activeSection === section.key && (
                  <motion.span
                    layoutId="activeTab"
                    className="absolute inset-0 bg-white/20 rounded-xl"
                  />
                )}
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      {/* News Sections */}
      <div className="container mx-auto px-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-16 grid grid-cols-2 gap-4"
        >
          {sectionConfig.map((section) => {
            const Icon = section.icon;
            const sectionData = categories[section.key];

            return (
              <motion.section
                key={section.key}
                variants={sectionVariants}
                className="group"
                onMouseEnter={() => setHoveredSection(section.key)}
                onMouseLeave={() => setHoveredSection(null)}
              >
                {/* Section Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className={`p-3 rounded-xl bg-gradient-to-br ${section.color} text-white`}
                    >
                      <Icon className="w-6 h-6" />
                    </motion.div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        {section.name}
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${hoveredSection === section.key ? "animate-pulse" : ""}`}
                          style={{
                            background: `linear-gradient(135deg, ${section.color})`,
                          }}
                        >
                          {section.count} articles
                        </motion.span>
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {section.description}
                      </p>
                    </div>
                  </div>
                  <motion.div whileHover={{ x: 5 }} className="hidden md:block">
                    {/* <Link
                      href={`/articles/${data[section.index]?.slug || section.key}`}
                      className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      View all
                      <ChevronRight className="w-4 h-4" />
                    </Link> */}
                  </motion.div>
                </div>

                {/* News Cards Grid */}
                {sectionData.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                    {sectionData.slice(0, 3).map((item, index) => (
                      <motion.div
                        key={item.id}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                        custom={index}
                        className="relative"
                      >
                        <Link
                          href={`/articles/${item.categories_slug}/${item.slug}`}
                          className="flex items-center gap-2"
                        >
                          {/* Card */}
                          <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 group/card">
                            {/* Image */}
                            {/* <motion.div
                            whileHover={{ x: 5 }}
                            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium text-sm"
                          > */}

                            <div className="relative h-48 overflow-hidden">
                              {item.image ? (
                                <motion.img
                                  src={item.image}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                  whileHover={{ scale: 1.1 }}
                                  transition={{ duration: 0.4 }}
                                />
                              ) : (
                                <div
                                  className={`w-full h-full bg-gradient-to-br ${section.color} opacity-10`}
                                />
                              )}
                              {/* Overlay gradient */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-300" />

                              {/* Category badge */}
                              <motion.div
                                initial={{ y: -10, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="absolute top-4 left-4"
                              >
                                <span
                                  className="px-3 py-1 rounded-full text-xs font-semibold text-white backdrop-blur-sm"
                                  style={{
                                    background: `linear-gradient(135deg, ${section.color})`,
                                  }}
                                >
                                  {item.name}
                                </span>
                              </motion.div>

                              {/* Date */}
                              <motion.div
                                initial={{ x: 10, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: index * 0.1 + 0.1 }}
                                className="absolute top-4 right-4 px-2 py-1 rounded-md bg-black/50 backdrop-blur-sm text-white text-xs"
                              >
                                {new Date(item.created_at).toLocaleDateString()}
                              </motion.div>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                              <motion.h3
                                className="text-lg font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover/card:text-blue-600 dark:group-hover/card:text-blue-400 transition-colors"
                                whileHover={{ x: 5 }}
                              >
                                {item.title}
                              </motion.h3>

                              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                {item.excerpt ||
                                  "Read this insightful article..."}
                              </p>

                              {/* Read More Link */}

                              {/* Read article */}
                            </div>
                            {/* <motion.div
                                  animate={{ x: 0 }}
                                  whileHover={{ x: 5 }}
                                  transition={{ type: "spring", stiffness: 300 }}
                                >
                                  {/* <ChevronRight className="w-4 h-4" /> 
                                </motion.div> */}
                            {/* </motion.div> */}
                          </div>
                        </Link>

                        {/* Hover effect */}
                        <div
                          className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${section.color} opacity-0 group-hover/card:opacity-10 -z-10 blur-xl transition-opacity duration-300`}
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
                    <Icon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      No articles available in {section.name.toLowerCase()} yet
                    </p>
                  </div>
                )}

                {/* View More Button (Mobile) */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-8 md:hidden"
                >
                  {/* <Link
                    href={`/articles/${data[section.index]?.slug || section.key}`}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    View all {section.name}
                    <ChevronRight className="w-4 h-4" />
                  </Link> */}
                </motion.div>
              </motion.section>
            );
          })}
        </motion.div>

        {/* Newsletter CTA */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-20"
        >
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-1">
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-8 md:p-12">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  Stay Updated with Our Newsletter
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                  Get the latest news, articles, and insights delivered directly
                  to your inbox
                </p>
                <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div> */}
      </div>
    </div>
  );
};

export default Sections;
